import { useState, useEffect, useMemo } from 'react';
import { ChartProps } from '../Interfaces/Charts';
import { Button } from 'react-bootstrap';
import { Download } from '../Utils/Downloader';
import VCenteredModal from '../Components/VCenteredModal';
import { DESCRIPTION_MESSAGE, DOWNLOAD_MESSAGE, EXANTE_LMP_SUFFIX, POPOUT_MESSAGE } from '../Constants';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';


export default function ExAnteLmp(props: ChartProps) {
	// ***************************************************************************************************************************************************
	// *** Custom Block: the following two defintions are custom to each table this code can produce,
	//      they provide the location for the data and the names of the columns to display
	const api_url = `${process.env.REACT_APP_PUBLIC_API_URL}${EXANTE_LMP_SUFFIX}`;
	const download_filename = 'ExAnteLMP';
	const popout_path = '/charts/exantelmp';
	const modalHeader: React.ReactNode = <span>ExAnte LMP</span>;

	interface IRow {
		name: string;
		LMP: string;
		loss: string;
		congestion: string;
	}

	function extractRowDataAndIntervalString(json: any) {
		const rowData = json.LMPData?.ExAnteLMP?.Hub ?? [];
		const intervalString = json.LMPData?.RefId ?? '';
		return { rowData, dataInterval: intervalString };
	}

	const descriptionBody: React.ReactNode = useMemo((): React.ReactNode => {
		return (
			<>
				<span>
					The Ex-Ante Locational Marginal Price (LMP) Chart displays the current Unit Dispatch System calculated pricing at each of the MISO footprint Hubs. The table
					will automatically refresh every 5 minutes to show updated information. Due to the nature of UDS approval, it is possible for the interval between 5 minute
					periods to vary widely. Under certain market circumstances the table display below may not display each 5 minute period.
				</span>
				<br />
				<br /> <span>This chart is updated every 5 minutes.</span>
			</>
		);
	}, []);
	// ***************************************************************************************************************************************************

	const [buttonClicked] = [props.buttonClicked];
	const [showModal, setShowModal] = useState(false);
	const [modalBody, setModalBody] = useState<React.ReactNode>(<></>);
	const [rawData, setRawData] = useState('');

	useEffect(() => {
		let downloadBody: React.ReactNode = (
			<div className='download-modal'>
				<Button className='download-btn' onClick={() => Download(api_url, download_filename, 'json')}>
					Download JSON
				</Button>
			</div>
		);

		switch (buttonClicked) {
			case DESCRIPTION_MESSAGE:
				setModalBody(descriptionBody);
				setShowModal(true);
				break;
			case DOWNLOAD_MESSAGE:
				setModalBody(downloadBody);
				setShowModal(true);
				break;
			case POPOUT_MESSAGE:
				const newWinddow = window.open(popout_path, '_blank', 'width=800,height=600,noopener,noreferrer');
				if (newWinddow) newWinddow.opener = null;
				break;
			default:
				break;
		}
	}, [buttonClicked, rawData, api_url, descriptionBody]);

	const useFetchJson = <T,>(url: string) => {
		const [data, setData] = useState<T[]>();
		const [loading, setLoading] = useState(false);
		const [dataInterval, setDataInterval] = useState<string>('');
		const [error, setError] = useState<string>('');

		useEffect(() => {
			let refreshIntervalId: NodeJS.Timeout;
			const fetchData = async () => {
				setLoading(true);
				setError(''); // Reset error state before new fetch
				try {
					const response = await fetch(url);
					if (!response.ok) throw new Error();
					const json = await response.json();
					setRawData(json);
					const { rowData, dataInterval } = extractRowDataAndIntervalString(json);
					setDataInterval(dataInterval);
					setData(rowData); // No limit, just set all data
				} catch (error) {
					setData([]); // On error, set data to empty array
					setError('Data Connection Failed'); // Set error message
				} finally {
					setLoading(false);
				}
			};
			fetchData();
			refreshIntervalId = setInterval(fetchData, 5 * 60 * 1000); // Repeat every 5 minutes
			return () => clearInterval(refreshIntervalId);
		}, [url]);

		return { data, loading, dataInterval, error };
	};

	const { data, loading, dataInterval, error } = useFetchJson<IRow>(api_url);

	if (loading) {
		return <div className='loading-and-error-box'>Loading...</div>;
	}
	if (error) {
		return <div className='loading-and-error-box'>{error}</div>;
	}
	if (!data || data.length === 0) {
		return <div className='loading-and-error-box'>No Data Available</div>;
	}

	return (
		<>
			<VCenteredModal show={showModal} onHide={() => setShowModal(false)} headercontent={modalHeader} bodycontent={modalBody} />
			<div className='interval-line'>{dataInterval}</div>	
				<Grid
					data={data}
					scrollable="none"
					resizable
					sortable={true}
					autoProcessData={true}
					style={{ width: '100%' }}
				>
					<Column field='name' title='Name' />
					<Column field='LMP' title='LMP' />
					<Column field='congestion' title='MCC' />
					<Column field='loss' title='MLC' />
				</Grid>
		
		</>
	);
}
